import React from 'react';
import styled from 'styled-components';
import { breakpoint } from '@themes';
import COLORS from '@themes/colors';
import { Button } from '@lesmills-international/components';
import { ReactComponent as HeartFilledIcon } from '../assets/icons/heart_filled.svg';
import { ReactComponent as HeartOutlinedIcon } from '../assets/icons/heart_outline.svg';

/**
 * Player wrapper styles
 * the height of the video player must be specified and we calculate based on propotion
 */
export const PlayerWrapper = styled.div`
  height: calc(100vw / 1.7778);
  margin-top: 12px;

  ${breakpoint('md')`
    height: calc((100vw - 160px) / 1.7778);
    margin-top: 0px;
  `}

  ${breakpoint('xl')`
    height: 720px;
  `}

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const PlayerErrorText = styled.p`
  color: ${COLORS.lightGreyAE};
  font-family: 'Inter Light';
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  padding: 0 24px;
`;

export const Description = styled.div`
  color: ${COLORS.lightGreyAE};
  font-size: 16px;
  font-weight: 500;
  width: 100%;
`;

interface DescriptionTextProps {
  'data-showfulldescription': boolean;
}

export const DescriptionText = styled.p<DescriptionTextProps>`
  margin-bottom: 16px;
  height: ${(props) => (props['data-showfulldescription'] ? 'auto' : '72px')};
  overflow: hidden;
`;

export const VideoTitle = styled.div`
  color: ${COLORS.lightishGrey};
  font-weight: 800;
  font-size: 24px;
  line-height: 100%;
  margin-bottom: 24px;
`;

export const SectionWrapper = styled.div`
  margin-top: 56px;
`;

export const CommentsWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 56px;
`;

export const PostCommentWrapper = styled.div`
  margin-top: 24px;
`;

export const RepliedCommentWrapper = styled.div`
  margin-left: 64px;
`;

export const Spacer = styled.div`
  margin-bottom: 16px;
`;

export const SpacerLeft = styled.div`
  margin-left: 12px;
`;

export const DeleteIconWrapper = styled.div`
  margin-left: 4px;
  margin-right: 16px;
`;

export const TopButtonsWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const NoCommentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 0px;
  margin-bottom: 56px;
  align-self: stretch;
`;

export const NoCommentsTitle = styled.p`
  color: ${COLORS.lightishGrey};
  font-family: Inter Light;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
`;

export const NoCommentsText = styled.p`
  color: ${COLORS.lightGreyAE};
  font-family: Inter Light;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
`;

export const Title = styled.p`
  font-family: 'Inter Medium';
  color: ${COLORS.lightishGrey};
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 0.08em;
`;

export const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

export const DropdownMenu = styled.div`
  width: 240px;
  background-color: ${COLORS.lightBlack};
  border-radius: 4px;
  padding: 8px 0;
  min-width: 120px;
  z-index: 1;
`;

// NEW API: Overlay dropdown menu that doesn't take DOM space
export const DropdownMenuOverlay = styled.div`
  position: absolute;
  top: 100%; // Position below the trigger button
  right: 0; // Align with the right edge of the trigger
  width: 160px; // Slightly smaller for better mobile UX
  background-color: ${COLORS.lightBlack};
  border-radius: 8px; // More modern rounded corners
  padding: 8px 0;
  z-index: 1000; // High z-index to appear above all content
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4); // Better shadow for overlay feel
  border: 1px solid ${COLORS.darkGrey2}; // Subtle border for definition
  
  /* Smooth appearance animation */
  animation: slideDown 0.15s ease-out;
  transform-origin: top right;
  
  /* Improved UX */
  backdrop-filter: blur(2px); // Subtle background blur for depth
  
  /* Responsive sizing */
  @media (max-width: 480px) {
    width: clamp(140px, 40vw, 160px);
    right: clamp(-10px, -2vw, 0px); // Slight left offset on mobile for better positioning
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5); // Stronger shadow on mobile for better visibility
  }
  
  /* Smooth exit animation */
  &.closing {
    animation: slideUp 0.12s ease-in;
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateY(-8px) scale(0.97);
    }
  }
`;

export const MenuItem = styled.div`
  color: ${COLORS.white};
  padding: 8px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s;
`;

export const DialogWrapper = styled.div`
  .cCVNOZ .MuiPaper-root {
    background-color: ${COLORS.lightBlack};
  }
`;

export const ContentContainer = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 32px;
  align-items: center;
  font-family: 'Inter Regular';
  color: ${COLORS.lightishGrey};
  font-size: 16px;
  font-weight: 500;
  flex-wrap: wrap;
`;

export const TrackContentContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  font-family: 'Inter Regular';
  color: ${COLORS.lightishGrey};
  font-size: 16px;
  font-weight: 500;
`;

export const EquipmentItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const NoEquipmentText = styled.p`
  color: ${COLORS.lightGreyAE};
`;

export const InstructorItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  & > img {
    border: 1px solid ${COLORS.greyLetter};
    border-radius: 50%;
  }
`;

export const ShowMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 16px;

  &:hover {
    background-color: ${COLORS.lightBlack};
    border-radius: 6px;
    cursor: pointer;
  }
`;

export const WorkoutWrapper = styled.div`
  width: 100%;
  font-family: 'Inter Regular';
  padding: 0;

  ${breakpoint('lg')`
    padding: 0px 80px;
  `}

  ${breakpoint('xl')`
    width: 1440px;
  `}
`;

export const ContentWrapper = styled.div`
  margin-top: 40px;
  padding: 0px;

  ${breakpoint('xs')`
    padding: 0px 16px;
  `}

  ${breakpoint('sm')`
    padding: 0px 32px;
  `}

  ${breakpoint('lg')`
    padding: 0;
  `}
`;

export const PageWrapper = styled.div`
  background: ${COLORS.black};
  display: flex;
  justify-content: center;
  min-height: 80vh;
`;

const TopButtonText = styled.div`
  font-family: 'Inter Medium';
  font-weight: 900;
  font-size: 14px;
  color: #ffffff;
`;

const IconWrapper = styled.div`
  margin-right: 8px;
`;

export const MyListButton = ({
  text,
  isProcessing,
  onClick,
  isFavourited,
}: {
  text: string;
  isProcessing?: boolean;
  onClick: () => void;
  isFavourited: boolean;
}) => (
  <Button
    style={{ borderRadius: '24px', height: '48px' }}
    loading={isProcessing}
    onClick={onClick}
    label={text}
  >
    {isFavourited ? (
      <IconWrapper>
        <HeartFilledIcon />
      </IconWrapper>
    ) : (
      <IconWrapper>
        <HeartOutlinedIcon />
      </IconWrapper>
    )}
    <TopButtonText>{text}</TopButtonText>
  </Button>
);
